import { storeToRefs } from 'pinia'
import type { ItemRow } from '~~/shared/items/itemRow'
import { useDocumentsStore, type DocumentSource } from '~/stores/documents'
import { useCasesStore, type Case } from '~/stores/cases'
import { useClientsStore } from '~/stores/clients'

/**
 * The document sidebar's groups, DERIVED from the cases, clients and documents stores (MAR-125).
 *
 * ## Why this is a composable and not store state
 *
 * A group is a case (or a client) plus its membership plus its document rows — three things that
 * already have owners. Holding a fourth, server-built copy is what broke: a dedicated
 * `/api/items/groups` route was the only source of group headers, nothing refetched it when a case was
 * created, and so a brand-new case simply did not exist for the sidebar until a reload. Deriving means
 * there is nothing to invalidate. That route is gone entirely as of MAR-93 — the documents come from
 * `/api/items?scope=org`.
 *
 * Creating, renaming or deleting a case now updates the sidebar on the same frame, because the header
 * IS the `Case` object. No refetch, no cache-busting, no "remember to refresh at every mutation site".
 *
 * It is a composable rather than a store getter for a concrete reason: `cases` already imports
 * `documents` (a case read seeds its document rows into the document cache). A `documents` getter that
 * read `cases` back would close an import cycle. Deriving one level up depends on all three and is
 * depended on by none of them.
 *
 * ## Preconditions
 *
 * The caller must have loaded cases (and, for project mode, clients). `PanelLayout` does this on mount
 * for the shell. A case that has not been loaded contributes no group — its documents still appear,
 * under Unfiled or not at all, which is why `ungroupedIds` is seeded from each row's server-reported
 * `case_ids` rather than inferred from "no case in the store claims it".
 */

export interface ResolvedDocumentGroup {
  /** Case id, client id, SHEET id, THREAD id, or a synthetic bucket key (`__none__` /
   *  `__ungrouped__` / `__unattached__` / `__conversations__`). */
  id: string
  /** `case`/`project`/`sheet`/`conversationThread` carry the real name; the synthetic buckets are
   *  labeled by the renderer. `sheet` joined them in AlexAmin/marcus#150 round 1, when it stopped
   *  being one bucket; `conversationThread` joins them in round 2, for the dedicated Conversaciones
   *  mode — the aggregate `conversation` bucket keeps its own renderer-supplied label. */
  name: string
  kind: 'case' | 'project' | 'none' | 'ungrouped' | 'unattached' | 'conversation' | 'sheet' | 'conversationThread'
  /** Case mode only: the case's client name. */
  subtitle: string | null
  /** The rows actually loaded — capped at one page per group (MAR-93). */
  documents: ItemRow[]
  /**
   * How many documents the group really holds. `total - documents.length` is the "+N more" the
   * renderer shows; it is never inferred from `documents`, which is the truncated view.
   */
  total: number
  /**
   * Project mode only: the client's matters, nested inside its group (AlexAmin/marcus#83 Finder
   * model — "client folder on the outside, matters inside it"). Each subgroup is a `case` group
   * exactly like `caseGroups` produces, so it renders and links the same way. `documents` on the
   * PARENT group then holds only what has no matter — the client-direct documents that are not
   * also linked to one of the client's cases — never the case-derived rows, which is what keeps a
   * both-linked document appearing exactly once, under its matter.
   */
  subgroups?: ResolvedDocumentGroup[]
}

export function useDocumentGroups() {
  const { t } = useI18n()
  const documents = useDocumentsStore()
  const casesStore = useCasesStore()
  const clientsStore = useClientsStore()
  const {
    orderedSources, byId: documentsById, ungroupedIds, ungroupedTotal,
    conversationLinkedIds, conversationTotal,
    sheetDocumentIds, sheetGroupsMeta, sheetLinkedIds, sheetTotal,
    conversationDocumentIds, conversationGroupsMeta,
    unattachedIds, unattachedTotal,
    clientDirectIds, clientDirectTotals, caseFilteredTotals, groupedRank,
  } = storeToRefs(documents)
  const { byId: casesById } = storeToRefs(casesStore)
  const { byId: clientsById } = storeToRefs(clientsStore)

  /**
   * Resolve ids to rows, dropping any the document cache does not hold, IN THE SERVER'S ORDER.
   *
   * ── WHY THE ORDER IS NOT THE ID LIST'S (AlexAmin/marcus#169) ───────────────────────────────────
   *
   * This used to walk `ids` and keep whatever it found, which meant every band was ordered by
   * whichever list fed it — `Case.documentIds` is `listCases`' `array_agg` by LINK TIME, so a case
   * band came out in the order its documents were attached, whatever the user had asked to sort by.
   * `FileExplorer` then re-sorted the result in the browser with a `[...docs].sort()` over the
   * band's loaded page: an ordering of a truncated prefix, presented as the band's order. Under an
   * icon menu that was merely unreliable. Under a column header with a sort arrow it is a claim, and
   * #169 puts a sort arrow there.
   *
   * So `listOrgDocuments` orders both its per-group windows and its final select by the active sort,
   * and this ranks by the order those rows arrived in (`groupedRank`). The client re-imposes
   * nothing: which rows a band can show and what order it shows them in are now one decision, taken
   * in SQL, and changing the sort refetches (`setSort`) instead of re-sorting what is already loaded.
   *
   * A row with no rank — reached only through the flat folder listing, or created locally since the
   * last org fetch — sorts last, in the id list's own order. The sort is stable, so that tail keeps
   * whatever order it had rather than being shuffled.
   */
  function resolve(ids: string[]): ItemRow[] {
    const out: ItemRow[] = []
    for (const id of ids) {
      const row = documentsById.value.get(id)
      if (row) out.push(row)
    }
    const rank = groupedRank.value
    return out
      .map((row, i) => ({ row, key: rank.get(row.id) ?? Number.MAX_SAFE_INTEGER, i }))
      .sort((a, b) => (a.key - b.key) || (a.i - b.i))
      .map(e => e.row)
  }

  /**
   * How many documents a case holds UNDER THE ACTIVE FILTER (AlexAmin/marcus#169).
   *
   * `Case.documentIds.length` was the only answer before the filters moved server-side, and it is
   * still the right one for a case the server has not named — one created in this session, whose
   * membership the browser maintains locally. But it comes from `listCases`, which knows nothing
   * about the finder's type/state lens, so under a filter it counts a population the band is not
   * rendering and turns "+N más" into a promise of documents the filter just excluded.
   *
   * Floored at the rows actually resolved, so a locally-linked document that has not been refetched
   * can never make the band read "-1 more".
   */
  function caseTotal(caseId: string, documentIds: string[], loaded: number): number {
    const fromServer = caseFilteredTotals.value.get(caseId)
    return Math.max(fromServer ?? documentIds.length, loaded)
  }

  const allCases = computed(() => [...casesById.value.values()])

  /**
   * Newest-activity first, matching the order the server used to return.
   *
   * `last_activity_at` moves when anything inside the case changes; `updated_at` only moves when the
   * case record itself does, so it is the fallback rather than the key.
   */
  function byActivity<T extends { last_activity_at?: string; updated_at: string }>(rows: T[]): T[] {
    return [...rows].sort((a, b) =>
      (b.last_activity_at ?? b.updated_at).localeCompare(a.last_activity_at ?? a.updated_at))
  }

  /** One group per case, empty cases included so the view doubles as a list of cases. */
  const caseGroups = computed<ResolvedDocumentGroup[]>(() =>
    byActivity(allCases.value).map(c => {
      const docs = resolve(c.documentIds)
      return {
        id: c.id,
        name: c.name,
        kind: 'case' as const,
        // Read through the clients store when the case row has not been told its client's name —
        // a freshly created case knows its `client_id` before it knows anything else about it.
        subtitle: c.client_name ?? (c.client_id ? clientsById.value.get(c.client_id)?.name ?? null : null),
        documents: docs,
        // `documentIds` is COMPLETE (listCases array_aggs every link, uncapped) while the rows are
        // capped at one page — so the count is honest even though the list is truncated, and no
        // server count is needed. `resolve` drops what it cannot find, which is exactly the
        // truncated tail. Under a type/state filter that stops being true — see `caseTotal`.
        total: caseTotal(c.id, c.documentIds, docs.length),
      }
    }),
  )

  /**
   * One group per client: its cases nested as **matter subgroups**, plus the documents linked
   * directly to the client with no matter of their own, sitting under the client itself.
   *
   * ── WHY BOTH HALVES, AND WHY NESTED NOT FLATTENED (AlexAmin/marcus#83) ─────────────────────────
   *
   * Until #83 a document reached a client only through one of its cases, so this unioned
   * `casesByClient` and nothing else. `client_documents` made a whole real state representable — the
   * client-level papers a firm holds before any matter exists — and a document in that state has an
   * empty `case_ids`, so it appeared in no client group at all.
   *
   * The union used to be FLATTENED into one list per client, which lost which matter (if any) a
   * document belonged to — the exact defect #83 called out. It now nests: each of the client's cases
   * becomes its own `subgroups` entry (built the same way `caseGroups` builds a case group, so it
   * renders and links identically), and the client's own `documents` holds only the direct-linked
   * ids that are NOT already reachable through one of those cases. A document linked to both a case
   * and its client is therefore still reachable exactly once — under the matter, per the case link
   * being the more specific one — never duplicated into the client's own row.
   *
   * `total` on the client group is the sum of its matters' totals plus its own direct-only total, so
   * the header count still reflects everything nested under it, matters included.
   *
   * A document attached to NOTHING is in no client group at all — it renders in `unattachedGroup`,
   * project mode's virtual "Sin cliente" group (MAR-1006). A client called "Documentos anteriores"
   * is an ordinary client here, like any other.
   */
  const projectGroups = computed<ResolvedDocumentGroup[]>(() => {
    const casesByClient = new Map<string, Case[]>()
    for (const c of byActivity(allCases.value)) {
      if (!c.client_id) continue
      const list = casesByClient.get(c.client_id) ?? []
      list.push(c)
      casesByClient.set(c.client_id, list)
    }

    const clients = [...clientsById.value.values()]
    return byActivity(clients).map(client => {
      const clientCases = casesByClient.get(client.id) ?? []
      const caseDocumentIds = new Set<string>()
      for (const c of clientCases) for (const id of c.documentIds) caseDocumentIds.add(id)

      const subgroups: ResolvedDocumentGroup[] = clientCases.map(c => {
        const docs = resolve(c.documentIds)
        return {
          id: c.id,
          name: c.name,
          kind: 'case' as const,
          subtitle: null,
          documents: docs,
          total: caseTotal(c.id, c.documentIds, docs.length),
        }
      })

      // Direct ids that are ALSO reachable through one of this client's matters are dropped here —
      // they already render once, under that matter's subgroup above.
      const direct = clientDirectIds.value.get(client.id) ?? []
      const directOnly = direct.filter(id => !caseDocumentIds.has(id))
      const alreadyCounted = direct.length - directOnly.length
      // The loaded direct ids are a prefix of the client's real direct membership; the tail we have
      // not loaded still counts towards the header's "+N more" even though we cannot know how much
      // of it overlaps a matter — only the overlap we can actually see (the loaded page) is
      // subtracted, same as the flat version this replaces did.
      const directTotal = Math.max(clientDirectTotals.value.get(client.id) ?? 0, direct.length)
      const directOnlyTotal = Math.max(directOnly.length, directTotal - alreadyCounted)

      // Summed from the SUBGROUPS, not from `documentIds` again: under a filter those two disagree,
      // and the client header has to add up to the bands rendered beneath it.
      const matterTotal = subgroups.reduce((sum, g) => sum + g.total, 0)
      return {
        id: client.id,
        name: client.name,
        kind: 'project' as const,
        subtitle: null,
        documents: resolve(directOnly),
        total: matterTotal + directOnlyTotal,
        subgroups,
      }
    })
  })

  /** Documents under a case with no client — the synthetic bucket, only when non-empty. */
  const noneGroup = computed<ResolvedDocumentGroup | null>(() => {
    const ids: string[] = []
    for (const c of allCases.value) {
      if (c.client_id) continue
      for (const id of c.documentIds) if (!ids.includes(id)) ids.push(id)
    }
    if (ids.length === 0) return null
    return { id: '__none__', name: '', kind: 'none', subtitle: null, documents: resolve(ids), total: ids.length }
  })

  /**
   * "Sin cliente" — the documents attached to NOTHING: no case, no client, no conversation, no sheet.
   * PROJECT MODE's virtual group, on every plan, only when non-empty (MAR-1006).
   *
   * ── WHY A VIRTUAL GROUP, AND NOT A CLIENT ROW ──────────────────────────────────────────────────
   *
   * #83 ruled out an "unattached" bucket and filed these documents under a real "Documentos
   * anteriores" HOLDING CLIENT instead (the `0141` sweep, then MAR-992 creating it on a case delete).
   * A stored client was the same bucket under another name, and it broke Light: a plan without
   * clients cannot have one, so an upload filed "Sin cliente" there rendered in no group at all.
   * Decided by Alex 2026-10-01: the bucket is virtual, built at read time from `unattachedIds` the
   * same way `ungroupedGroup` is built from `ungroupedIds`, and nothing is created to hold it. The
   * holding-client rows that already existed were deliberately left in the database as ordinary
   * clients; they render under their own name with their own links, never with these.
   *
   * Each of these documents appears here and nowhere else in this mode: having no case it is in no
   * matter subgroup and in no `noneGroup`; having no client it is in no client's `documents`; having
   * no thread and no sheet it is in neither of those groups.
   *
   * The total comes from the server (`unattachedTotal`), because the loaded ids are capped at one
   * page; it floors at the loaded count so a locally-marked document can never read as "-1 more".
   */
  const unattachedGroup = computed<ResolvedDocumentGroup | null>(() => {
    const docs = resolve(unattachedIds.value)
    if (docs.length === 0) return null
    return {
      id: '__unattached__', name: '', kind: 'unattached', subtitle: null,
      documents: docs, total: Math.max(unattachedTotal.value, docs.length),
    }
  })

  /**
   * Documents linked to no case at all — CASE MODE's trailing bucket. Only when non-empty.
   *
   * ── "NO CASE" AND "ATTACHED TO NOTHING" ARE NOT THE SAME BUCKET (AlexAmin/marcus#83) ───────────
   *
   * They used to be, because a document could only be attached to a case. Now it can be attached
   * directly to a client or to a chat thread, and a client-only document is FILED — it simply has no
   * case. Case mode groups by case, so "not in a case" is the honest description of this bucket, and
   * a client-only document belongs in it. **Do not collapse it into "attached to nothing"** — that
   * is what made a client-only document vanish from the page rather than move.
   *
   * ── AND PROJECT MODE'S OWN TRAILING BUCKET IS NARROWER ──────────────────────────────────────────
   *
   * Project mode asks the other question — "attached to nothing" — in `unattachedGroup` ("Sin
   * cliente", MAR-1006). An attached-to-nothing document is in both buckets, one per mode, because it
   * has neither a case nor a client; a client-only document is only in this one.
   *
   * `ungroupedTotal` therefore stays keyed on "no case" and is NOT narrowed to "attached to nothing":
   * that is `unattachedTotal`, a different question with a different home. Narrowing this one would
   * make case mode's count disagree with the list it labels.
   *
   * The total comes from the server because no case row carries it. It floors at the loaded count so
   * a locally-unlinked document (which pushes an id into the list without refetching) can never read
   * as "-1 more".
   */
  const ungroupedGroup = computed<ResolvedDocumentGroup | null>(() => {
    const docs = resolve(ungroupedIds.value)
    if (docs.length === 0) return null
    return {
      id: '__ungrouped__', name: '', kind: 'ungrouped', subtitle: null,
      documents: docs, total: Math.max(ungroupedTotal.value, docs.length),
    }
  })

  /**
   * "From your conversations" — every document linked to a chat thread.
   *
   * ── A HOME, NOT A FALLBACK (AlexAmin/marcus#150) ────────────────────────────────────────────────
   *
   * This read `conversationOnlyIds` — attached to a thread and to NOTHING else — so a document that
   * was also filed into a matter left the group. Filing into a matter is the normal state under #83,
   * which made the group permanently empty on any real organization. It now lists the thread's
   * documents whatever else they belong to, and membership consequently stops being a partition: one
   * document is reachable both under its matter and here.
   *
   * ── AND WHY THIS ONE IS STILL A SINGLE BUCKET WHILE SHEETS GET A GROUP EACH ─────────────────────
   *
   * A sheet is a durable, named, user-made container — the same kind of thing as a case or a client,
   * so it gets the same treatment. A chat thread is not: it is created by asking a question, titled
   * from that question, and holds the one file the question was about (on the local database, one
   * thread, one document). One group per thread would fill the sidebar with single-document groups
   * headed by a truncated first message — noise that answers a question nobody asked, when the one
   * the user does ask is "where did the file I dropped into a chat go?". One bucket answers that.
   *
   * Project mode only, in both cases. In case mode a document with a case is already under it and a
   * document without one is already in `ungroupedGroup`, so emitting these too would list it twice
   * on one screen — the invariant #148 pinned.
   */
  const conversationGroup = computed<ResolvedDocumentGroup | null>(() => {
    const docs = resolve(conversationLinkedIds.value)
    if (docs.length === 0) return null
    return {
      id: '__conversations__', name: '', kind: 'conversation', subtitle: null,
      documents: docs, total: Math.max(conversationTotal.value, docs.length),
    }
  })

  /**
   * ONE GROUP PER SHEET, named after the sheet (AlexAmin/marcus#150).
   *
   * The predecessor was a single "De tus hojas" bucket fed by `sheetOnlyIds`, and it had both of the
   * defects above: the fallback predicate emptied it, and a pile named after the *kind* of home
   * cannot tell you which of your nine sheets a document came from. A sheet has a name, a page and
   * a lifetime, exactly like a case, so it is grouped like one.
   *
   * The header count is the sheet's own uncapped total, shipped by the server — the loaded ids are
   * the leading prefix the per-sheet cap left us, so `total - documents.length` is a real "+N more".
   * Sheets holding nothing never reach here; the server omits them.
   */
  const sheetGroups = computed<ResolvedDocumentGroup[]>(() =>
    sheetGroupsMeta.value.map(sheet => {
      const docs = resolve(sheetDocumentIds.value.get(sheet.id) ?? [])
      return {
        id: sheet.id,
        name: sheet.name,
        kind: 'sheet' as const,
        subtitle: null,
        documents: docs,
        total: Math.max(sheet.total, docs.length),
      }
    }).filter(g => g.total > 0),
  )

  /**
   * ONE GROUP PER CHAT THREAD, named after the thread (AlexAmin/marcus#150 round 2).
   *
   * Only used by the dedicated `conversation` group mode — the mode round 2 adds so Conversaciones
   * can be selected the way Clientes and Casos already are. This is deliberately NOT a replacement
   * for `conversationGroup` below: a thread is created by asking a question and usually holds one
   * file, so the aggregate bucket still exists and still renders inside Clientes mode (see that
   * computed's own docblock for why one-group-per-thread is wrong THERE). Here it is not wrong: the
   * user asked for exactly this breakdown by picking Conversaciones from the switcher, the same way
   * picking Hojas asks for one row per sheet.
   *
   * `name` falls back to `t('common.untitled')` for a thread with no title — the same fallback
   * `ChatPanel`/`IconRail` already use for a titleless thread in their own lists, not a new string.
   * A thread with no title is not "nothing to group by"; it is a real thread that happens to be
   * unnamed, which is why this renders it rather than folding it into a bucket like `noneGroup` does
   * for client-less cases.
   */
  const conversationGroups = computed<ResolvedDocumentGroup[]>(() =>
    conversationGroupsMeta.value.map(conv => {
      const docs = resolve(conversationDocumentIds.value.get(conv.id) ?? [])
      return {
        id: conv.id,
        name: conv.name.trim() || t('common.untitled'),
        kind: 'conversationThread' as const,
        subtitle: null,
        documents: docs,
        total: Math.max(conv.total, docs.length),
      }
    }).filter(g => g.total > 0),
  )

  /**
   * The finder's single "de tus hojas" signpost (AlexAmin/marcus#145), now that there is no single
   * sheet group to point at.
   *
   * It is NOT a group and is never rendered as one: `total` is the count of DISTINCT documents that
   * sit in some sheet, which is smaller than the sum of the per-sheet totals whenever a document is
   * in two sheets. `id` is the first sheet group's, purely as a scroll anchor for the hand-off into
   * Clientes mode.
   */
  const sheetsSignpost = computed<{ id: string; total: number } | null>(() => {
    const first = sheetGroups.value[0]
    if (!first) return null
    const loaded = resolve(sheetLinkedIds.value).length
    return { id: first.id, total: Math.max(sheetTotal.value, loaded) }
  })

  /**
   * The groups ONE source contributes, in its own render order.
   *
   * These are the four lists the pre-#158 `groupMode` switch produced, unchanged and deliberately
   * so: what a source's groups CONTAIN is #150's territory, and #158 only decides how several
   * sources compose. `project` therefore still carries the aggregate conversations bucket and the
   * per-sheet groups (see each computed's own docblock for why), while `sheet` and `conversation`
   * still carry no trailing "no case" bucket — neither has an honest reading of "ungrouped".
   */
  function groupsForSource(source: DocumentSource): ResolvedDocumentGroup[] {
    if (source === 'sheet') return sheetGroups.value
    if (source === 'conversation') return conversationGroups.value
    if (source === 'case') {
      const out = [...caseGroups.value]
      if (ungroupedGroup.value) out.push(ungroupedGroup.value)
      return out
    }
    const out = [...projectGroups.value]
    if (noneGroup.value) out.push(noneGroup.value)
    if (unattachedGroup.value) out.push(unattachedGroup.value)
    if (conversationGroup.value) out.push(conversationGroup.value)
    out.push(...sheetGroups.value)
    return out
  }

  /**
   * The groups for the active SELECTION, in render order (AlexAmin/marcus#158).
   *
   * ── GROUPED SECTIONS, NOT A MERGED LIST ────────────────────────────────────────────────────────
   *
   * Two selected sources render as two runs of grouped sections, concatenated in the canonical
   * source order (`DOCUMENT_SOURCES`) — never flattened into one undifferentiated list. Every source
   * here is a set of NAMED containers (a client, a case, a sheet, a thread), and the header is the
   * only thing that says which one a document came from; merging would throw away the answer to the
   * question the user asked by selecting the source.
   *
   * ── NO SOURCES SELECTED RENDERS NOTHING ────────────────────────────────────────────────────────
   *
   * The flat listing at `currentPath` is the whole view then, and a document sitting at that path
   * would otherwise be listed twice on one screen — once loose, once under its bucket. The finder
   * reaches the two homes the folder tree cannot show through `conversationGroup`/`sheetsSignpost`
   * instead, as signposts rather than document lists (AlexAmin/marcus#145).
   *
   * ── THERE IS NO CROSS-SOURCE DEDUP, AND ITS ABSENCE IS THE DECISION ────────────────────────────
   *
   * There was one until AlexAmin/marcus#168: a `dropSeen()` helper walked the sources in
   * `DOCUMENT_SOURCES` order and removed from each group every document an earlier source had
   * already rendered, dropping any band the removal emptied. It is gone, and nothing should bring it
   * back without reopening #150.
   *
   * What it did in practice: select Conversaciones alone and the `was dis` thread renders its one
   * document; ALSO select Hojas, and that thread disappears entirely — its only document also sits
   * in a sheet, sheets sort before conversations, so the sheet claimed it and the emptied band was
   * dropped. Ticking an extra source deleted what the previously ticked source was showing, and a
   * user who asks for Conversaciones and sees nothing cannot tell "there is nothing there" from
   * "something absorbed it".
   *
   * The deeper reason it had to go is that it contradicted AlexAmin/marcus#150, which shipped
   * (`cb623948`, `da0046a9`) the rule that **a sheet is a home, not a fallback** — a document has
   * several homes and belongs under each of them. Cross-source dedup re-imposed one-home-wins inside
   * a single view, just with a different winner, and the winner was an accident: `DOCUMENT_SOURCES`
   * is ordered client → case → sheet → conversation because a client is the outer CONTAINER (#81),
   * never because a sheet outranks a thread.
   *
   * The cost, accepted deliberately (Alex, 2026-09-07): the same file can appear twice on one screen,
   * under two named bands, and a band's count therefore means MEMBERSHIPS rather than distinct
   * documents. The band header is what makes the repeat legible — it names which home you are
   * looking at, which is the answer to the question the user asked by selecting the source.
   *
   * `seenGroups` stays. A GROUP reached from two sources (a case selected both as a case and through
   * its client) is one band, and emitting it twice would be a duplicate band, not a document with two
   * homes.
   */
  const groups = computed<ResolvedDocumentGroup[]>(() => {
    const selected = orderedSources.value
    if (selected.length === 0) return []
    if (selected.length === 1) return groupsForSource(selected[0]!)

    const seenGroups = new Set<string>()
    const out: ResolvedDocumentGroup[] = []
    for (const source of selected) {
      for (const group of groupsForSource(source)) {
        const key = `${group.kind}:${group.id}`
        if (seenGroups.has(key)) continue
        seenGroups.add(key)
        out.push(group)
      }
    }
    return out
  })

  /**
   * `conversationGroup`, `sheetGroups` and `sheetsSignpost` are returned ALONGSIDE `groups`, not
   * folded into it.
   *
   * They are the two homes with no representation in the folder tree, so the finder needs to name
   * them from folders mode (AlexAmin/marcus#145) — but only as a signpost that switches to Clientes
   * mode, where they render for real. Handing them out separately is what keeps that possible
   * without widening `groups`, whose project-mode-only membership is the invariant that stops case
   * mode listing these documents twice (in case mode a sheet's documents are already under their
   * own case, or in `ungroupedGroup` when they have none).
   *
   * `conversationGroups` is returned the same way, for the same reason: it is what `groups` reads
   * when the mode is `conversation`, but a caller building its own signpost (were one ever needed
   * for the per-thread breakdown) can read it directly instead of filtering `groups`.
   */
  return { groups, conversationGroup, sheetGroups, sheetsSignpost, conversationGroups }
}
